import type { Contact, ContactInput } from './types.ts';

export type ImportedContact = Partial<ContactInput> & { name: string; emails: string[] };

/** RFC 4180 CSV parser (quoted fields, escaped quotes, CRLF/LF, BOM). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  const s = text.replace(/^\uFEFF/, '');
  const nl = s.indexOf('\n');
  const head = nl >= 0 ? s.slice(0, nl) : s;
  const count = (ch: string) => head.split(ch).length - 1;
  const delim = count('\t') > count(',') && count('\t') > count(';') ? '\t' : count(';') > count(',') ? ';' : ',';
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (quoted) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += c;
    } else if (c === '"') quoted = true;
    else if (c === delim) {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && s[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += c;
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((v) => v.trim() !== ''));
}

type Field = 'name' | 'first' | 'middle' | 'last' | 'email' | 'phone' | 'company' | 'title' | 'website' | 'address' | 'birthday' | 'notes' | 'tags';

/** Header -> field rules covering Google Contacts, Outlook, Apple/iCloud exports, HubSpot/Salesforce and generic sheets. */
const RULES: [RegExp, Field][] = [
  [/^(display ?name|full ?name|name|contact ?name|contact)$/, 'name'],
  [/^(first ?name|given ?name|firstname|first)$/, 'first'],
  [/^(middle ?name|additional ?name)$/, 'middle'],
  [/^(last ?name|family ?name|surname|lastname|last)$/, 'last'],
  [/(e-?mail|email ?address)/, 'email'],
  [/(phone|mobile|cell|tel)/, 'phone'],
  [/^(company|organi[sz]ation|organi[sz]ation ?1? ?-? ?name|organi[sz]ation ?name|account ?name|employer|business)$/, 'company'],
  [/(job ?title|^title$|organi[sz]ation ?1? ?-? ?title|position|role)/, 'title'],
  [/(web ?site|web ?page|url|homepage)/, 'website'],
  [/(^address|street|formatted|address ?1? ?-? ?formatted)/, 'address'],
  [/(birthday|birth ?date|dob)/, 'birthday'],
  [/^(notes?|description|comments?)$/, 'notes'],
  [/(tags?|labels?|groups?|categor(y|ies)|group ?membership)/, 'tags'],
];

export function detectCsvMapping(headers: string[]): (Field | null)[] {
  return headers.map((h) => {
    const n = h.toLowerCase().replace(/[_.]/g, ' ').replace(/\s+/g, ' ').trim();
    if (/(type|label)$/.test(n) && /(e-?mail|phone|address|website)/.test(n)) return null; // "E-mail 1 - Type"
    for (const [re, f] of RULES) if (re.test(n)) return f;
    return null;
  });
}

export interface CsvImport {
  headers: string[];
  mapping: (string | null)[];
  contacts: ImportedContact[];
}

export function csvToContacts(text: string): CsvImport {
  const rows = parseCsv(text);
  if (rows.length === 0) return { headers: [], mapping: [], contacts: [] };
  const headers = rows[0];
  const mapping = detectCsvMapping(headers);
  const contacts: ImportedContact[] = [];
  for (const r of rows.slice(1)) {
    const c: ImportedContact = { name: '', emails: [], phones: [], tags: [] };
    let first = '', middle = '', last = '';
    mapping.forEach((f, i) => {
      const v = (r[i] ?? '').trim();
      if (!v || !f) return;
      switch (f) {
        case 'name': c.name = v; break;
        case 'first': first = v; break;
        case 'middle': middle = v; break;
        case 'last': last = v; break;
        case 'email': c.emails.push(...v.split(/\s*(?::::|[,;\s])\s*/).filter((e) => e.includes('@'))); break;
        case 'phone': c.phones!.push(...v.split(/\s*:::\s*/)); break;
        case 'company': c.company = v; break;
        case 'title': c.title = v; break;
        case 'website': c.website = v; break;
        case 'address': c.address = v; break;
        case 'birthday': c.birthday = v; break;
        case 'notes': c.notes = c.notes ? `${c.notes}\n${v}` : v; break;
        case 'tags': c.tags!.push(...v.split(/\s*(?::::|[,;|])\s*/).map((t) => t.replace(/^\* ?/, '')).filter((t) => t && !/^myContacts$/i.test(t))); break;
      }
    });
    if (!c.name) c.name = [first, middle, last].filter(Boolean).join(' ');
    c.emails = dedupe(c.emails.map((e) => e.toLowerCase()));
    c.phones = dedupe(c.phones ?? []);
    if (!c.name && c.emails.length) c.name = c.emails[0];
    if (c.name || c.emails.length) contacts.push(c);
  }
  return { headers, mapping, contacts };
}

/** vCard 2.1 / 3.0 / 4.0 parser. */
export function parseVCards(text: string): ImportedContact[] {
  const unfolded = text.replace(/\r\n/g, '\n').replace(/\n[ \t]/g, '').replace(/=\n/g, '');
  const cards = unfolded.split(/BEGIN:VCARD/i).slice(1);
  return cards.map((card) => {
    const c: ImportedContact = { name: '', emails: [], phones: [], tags: [] };
    let n = '';
    for (const line of card.split('\n')) {
      const idx = line.indexOf(':');
      if (idx < 0) continue;
      const left = line.slice(0, idx);
      const params = left.split(';');
      const key = params[0].replace(/^item\d+\./i, '').toUpperCase();
      let value = line.slice(idx + 1).trim();
      if (/ENCODING=QUOTED-PRINTABLE/i.test(left)) value = decodeQp(value);
      const unesc = (v: string) => v.replace(/\\n/gi, '\n').replace(/\\([,;\\])/g, '$1').trim();
      switch (key) {
        case 'FN': c.name = unesc(value); break;
        case 'N': n = value.split(';').map(unesc).filter(Boolean).slice(0, 3).reverse().join(' '); break;
        case 'EMAIL': if (value.includes('@')) c.emails.push(unesc(value).replace(/^mailto:/i, '').toLowerCase()); break;
        case 'TEL': c.phones!.push(unesc(value).replace(/^tel:/i, '')); break;
        case 'ORG': c.company = value.split(';').map(unesc).filter(Boolean).join(', '); break;
        case 'TITLE': c.title = unesc(value); break;
        case 'URL': c.website = unesc(value); break;
        case 'ADR': c.address = value.split(';').map(unesc).filter(Boolean).join(', '); break;
        case 'BDAY': c.birthday = unesc(value); break;
        case 'NOTE': c.notes = unesc(value); break;
        case 'CATEGORIES': c.tags!.push(...value.split(',').map(unesc).filter(Boolean)); break;
      }
    }
    if (!c.name) c.name = n || c.emails[0] || '';
    c.emails = dedupe(c.emails);
    c.phones = dedupe(c.phones ?? []);
    return c;
  }).filter((c) => c.name || c.emails.length);
}

export function parseContactsFile(fileName: string, text: string): ImportedContact[] {
  if (/\.vcf$|\.vcard$/i.test(fileName) || /BEGIN:VCARD/i.test(text.slice(0, 500))) return parseVCards(text);
  return csvToContacts(text).contacts;
}

export function contactsToVCard(contacts: Contact[]): string {
  const esc = (v: string) => v.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/([,;])/g, '\\$1');
  return contacts.map((c) => {
    const parts = c.name.split(/\s+/);
    const last = parts.length > 1 ? parts.pop()! : '';
    const lines = ['BEGIN:VCARD', 'VERSION:3.0', `FN:${esc(c.name)}`, `N:${esc(last)};${esc(parts.join(' '))};;;`];
    c.emails.forEach((e) => lines.push(`EMAIL;TYPE=INTERNET:${e}`));
    c.phones.forEach((p) => lines.push(`TEL:${esc(p)}`));
    if (c.company) lines.push(`ORG:${esc(c.company)}`);
    if (c.title) lines.push(`TITLE:${esc(c.title)}`);
    if (c.website) lines.push(`URL:${esc(c.website)}`);
    if (c.address) lines.push(`ADR:;;${esc(c.address)};;;;`);
    if (c.birthday) lines.push(`BDAY:${esc(c.birthday)}`);
    if (c.notes) lines.push(`NOTE:${esc(c.notes)}`);
    if (c.tags.length) lines.push(`CATEGORIES:${c.tags.map(esc).join(',')}`);
    lines.push('END:VCARD');
    return lines.join('\r\n');
  }).join('\r\n');
}

export function contactsToCsv(contacts: Contact[]): string {
  const cols = ['Name', 'Email', 'Email 2', 'Phone', 'Phone 2', 'Company', 'Title', 'Website', 'Address', 'Birthday', 'Tags', 'Notes'];
  const q = (v: string | undefined) => `"${(v ?? '').replace(/"/g, '""')}"`;
  const rows = contacts.map((c) => [c.name, c.emails[0], c.emails[1], c.phones[0], c.phones[1], c.company, c.title, c.website, c.address, c.birthday, c.tags.join('; '), c.notes].map(q).join(','));
  return [cols.join(','), ...rows].join('\r\n');
}

function decodeQp(v: string): string {
  const bytes: number[] = [];
  for (let i = 0; i < v.length; i++) {
    if (v[i] === '=' && /^[0-9A-F]{2}$/i.test(v.slice(i + 1, i + 3))) {
      bytes.push(parseInt(v.slice(i + 1, i + 3), 16));
      i += 2;
    } else bytes.push(v.charCodeAt(i));
  }
  return new TextDecoder().decode(new Uint8Array(bytes));
}

function dedupe(list: string[]): string[] {
  const seen = new Set<string>();
  return list.map((x) => x.trim()).filter((x) => x && !seen.has(x.toLowerCase()) && seen.add(x.toLowerCase()) !== undefined);
}
